import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, verifyMfa } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUserId, setMfaUserId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Helper to safely get role and redirect
  const handleSuccessfulLogin = (result) => {
    // Check if user must change password first
    if (result.mustChangePassword) {
      navigate('/change-password', { state: { forced: true } });
      return;
    }
    
    // Safely get role from multiple sources
    const role = result?.role || result?.user?.role || null;

    if (!role) {
      console.warn('No role found in login result, defaulting to dashboard');
    }
    // All roles land on /dashboard; HomeRouter renders the role-aware home
    // (Founder Command Center for admins/staff, ApplicantDashboard for drivers/applicants).
    if (role === 'partner_user') { navigate('/partner'); return; }
    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      handleSuccessfulLogin(result);
    } else if (result.mfaRequired) {
      // MFA is required - show MFA input
      setMfaRequired(true);
      setMfaUserId(result.userId);
      setError('');
    } else {
      setError(result.error || 'Login failed');
    }

    setLoading(false);
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await verifyMfa(mfaUserId, mfaCode);

    if (result.success) {
      handleSuccessfulLogin(result);
    } else {
      setError(result.error || 'MFA verification failed');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-blue-900">BlueLock Freights</h1>
          </Link>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">Welcome Back</h2>
          <p className="mt-2 text-sm text-gray-600">
            Log in to manage your application or employment information.
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" data-testid="login-error">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={mfaRequired ? handleMfaSubmit : handleSubmit} className="space-y-6">
            {!mfaRequired ? (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    data-testid="login-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    data-testid="login-password-input"
                  />
                  <div className="mt-2 text-right">
                    <Link to="/forgot-password" className="text-sm font-medium text-blue-600 hover:text-blue-500" data-testid="login-forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Enter the 6-digit code from your authenticator app
                </p>
                <input
                  id="mfaCode"
                  name="mfaCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                  data-testid="login-mfa-input"
                  autoComplete="one-time-code"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? 'Verifying...' : (mfaRequired ? 'Verify Code' : 'Log In')}
            </button>

            {mfaRequired && (
              <button
                type="button"
                onClick={() => {
                  setMfaRequired(false);
                  setMfaCode('');
                  setMfaUserId(null);
                  setError('');
                }}
                className="w-full flex justify-center py-2 px-4 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                ← Back to Login
              </button>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                Create Your Account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
